// File: frontend/src/components/ui/AppDialog.js
//
// The one dialog shell. A structured wrapper over MUI `<Dialog>` that gives every
// modal the SAME anatomy — a header (optional tinted icon badge + title/subtitle +
// close button), a scrollable body, and a footer actions row — so the ~20 bespoke
// `<Dialog>` usages across the pages can be retired onto a single, theme-driven
// primitive. No hardcoded colours: surface, radius, dividers and padding all come
// from the theme. ConfirmDialog is conceptually a specialisation of this shell.
//
//   <AppDialog
//     open={open}
//     onClose={() => setOpen(false)}
//     title="Enable Roaming?"
//     subtitle="Applies to every router in this business"
//     icon={<RoamingIcon />}
//     size="sm"
//     actions={
//       <>
//         <AppButton intent="ghost" onClick={close}>Cancel</AppButton>
//         <AppButton intent="primary" onClick={save}>Enable</AppButton>
//       </>
//     }
//   >
//     …body…
//   </AppDialog>

import React from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, IconButton, Divider, useTheme, useMediaQuery, alpha,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";

// `size` is the ergonomic knob; it maps onto MUI's `maxWidth`. An explicit
// `maxWidth` prop always wins for the rare bespoke case.
const SIZE_TO_MAXWIDTH = { sm: "xs", md: "sm", lg: "md", xl: "lg" };

// Header icon-badge tint, mirroring ConfirmDialog's tone scale so the two dialogs
// read as siblings. Falls back to the brand primary.
const TONE = {
  primary: (t) => t.palette.primary.main,
  danger:  (t) => t.palette.error.main,
  warning: (t) => t.palette.warning.main,
  info:    (t) => t.palette.info.main,
  success: (t) => t.palette.success.main,
};

export default function AppDialog({
  open,
  onClose,
  title,
  subtitle,
  icon,                    // optional node rendered inside a tinted badge
  iconTone = "primary",    // primary | danger | warning | info | success
  children,                // body
  actions,                 // footer node (buttons); omit for a chromeless footer
  size = "md",             // sm | md | lg | xl → maps to MUI maxWidth
  maxWidth,                // explicit MUI maxWidth override (xs/sm/md/lg/xl)
  fullWidth = true,
  showClose = true,        // header close icon-button
  loading = false,         // when true, blocks close (backdrop/esc/close button)
  dividers = true,         // header + footer separators
  contentSx,               // sx merged onto the scrollable body
  // ── Phone editors ─────────────────────────────────────────────────────────
  // An editing dialog on a phone should BE the screen, not a floating card with
  // wasted margin on all four sides. Opt in with `mobileFullScreen`: the header
  // goes compact, its close button becomes a Back arrow on the left where a
  // thumb expects it, the body takes the full width, and the actions row stays
  // pinned at the bottom clear of the home indicator.
  //
  // Deliberately opt-in. A confirmation with two lines of text should not become
  // a full screen; only an EDITOR the user works down should.
  mobileFullScreen = false,
  fullScreen,              // explicit override — still just MUI's fullScreen
  PaperProps,
  ...dialogProps           // pass-through to MUI <Dialog> (aria-*, TransitionProps, …)
}) {
  const theme = useTheme();
  const tone = (TONE[iconTone] || TONE.primary)(theme);
  const hasHeader = Boolean(title || subtitle || icon || showClose);
  const phone = useMediaQuery(theme.breakpoints.down("sm"));

  // Two different questions, deliberately kept apart:
  //
  //   isFullScreen  does the Dialog fill the viewport? (what MUI is told)
  //   asSheet       does it get PHONE-EDITOR chrome — compact header, a Back
  //                 arrow instead of a corner X, an edge-to-edge body, a footer
  //                 clear of the home indicator?
  //
  // Callers have passed `fullScreen={isMobile}` here for a long time and expect
  // only the first. Collapsing the two would silently restyle the header of
  // every one of them, so the sheet chrome is opt-in via `mobileFullScreen`.
  const isFullScreen = fullScreen !== undefined ? fullScreen : (mobileFullScreen && phone);
  const asSheet = mobileFullScreen && isFullScreen;

  // Swallow close requests while loading so a submit-in-flight can't be dismissed.
  const handleClose = loading ? undefined : onClose;
  const closeDisabled = loading || !onClose;

  return (
    <Dialog
      open={!!open}
      onClose={handleClose}
      maxWidth={maxWidth || SIZE_TO_MAXWIDTH[size] || "sm"}
      fullWidth={fullWidth}
      fullScreen={isFullScreen}
      PaperProps={{
        ...PaperProps,
        sx: [
          asSheet && { m: 0, borderRadius: 0, width: "100%", maxWidth: "100%", height: "100%", maxHeight: "100%" },
          ...(Array.isArray(PaperProps?.sx) ? PaperProps.sx : [PaperProps?.sx]),
        ].filter(Boolean),
      }}
      {...dialogProps}
    >
      {hasHeader && (
        <>
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.25,
              minWidth: 0,
              // Compact top header on a phone: the screen is for the form, not
              // for chrome.
              ...(asSheet ? { px: 1, py: 1 } : null),
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: asSheet ? 0.5 : 1.25, minWidth: 0 }}>
              {asSheet && onClose && (
                <IconButton
                  onClick={onClose}
                  disabled={closeDisabled}
                  aria-label="Back"
                  // Measured at 38px on a 390px viewport before this: MUI's
                  // default IconButton box, not the 44px a thumb needs on the
                  // one control that leaves a full-screen editor.
                  sx={{ color: "text.primary", flexShrink: 0, width: 44, height: 44 }}
                >
                  <ArrowBackRoundedIcon sx={{ fontSize: 22 }} />
                </IconButton>
              )}
              {icon && !asSheet && (
                <Box
                  sx={{
                    width: 34, height: 34, borderRadius: 2, flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    bgcolor: alpha(tone, 0.12), color: tone,
                    "& svg": { fontSize: 19 },
                  }}
                >
                  {icon}
                </Box>
              )}
              <Box sx={{ minWidth: 0 }}>
                {title && (
                  <Box component="span" sx={{ display: "block", minWidth: 0 }}>
                    {title}
                  </Box>
                )}
                {subtitle && (
                  <Typography
                    sx={{ fontSize: 12.5, fontWeight: 400, color: "text.secondary", mt: 0.25 }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Box>
            </Box>
            {/* Back already leads out of a full-screen editor; a second exit in
                the opposite corner is two answers to one question. */}
            {showClose && onClose && !asSheet && (
              <IconButton
                onClick={onClose}
                disabled={closeDisabled}
                size="small"
                aria-label="Close"
                // The glyph stays 20px; the box grows to the tap-target floor.
                // Measured at 30x30 on a touch tablet, which is under the AA
                // minimum let alone the AAA one.
                sx={{ color: "text.secondary", flexShrink: 0, ml: 1, width: 44, height: 44 }}
              >
                <CloseRoundedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            )}
          </DialogTitle>
          {dividers && <Divider sx={{ borderColor: "divider" }} />}
        </>
      )}

      <DialogContent
        sx={{
          // Restore top padding the theme trims when it assumes a title sits above.
          pt: hasHeader ? 2.5 : undefined,
          ...(asSheet ? { px: 2, pb: 2 } : null),
          ...contentSx,
        }}
      >
        {children}
      </DialogContent>

      {actions && (
        <>
          {dividers && <Divider sx={{ borderColor: "divider" }} />}
          <DialogActions
            sx={{
              // The sticky action stays touch-friendly without eating the screen:
              // one row of padding, plus whatever the home indicator needs. The
              // Paper is a flex column, so the scrollable body always ends ABOVE
              // this row — nothing can hide behind it.
              ...(asSheet
                ? {
                    px: 2, pt: 1.25,
                    pb: "calc(10px + env(safe-area-inset-bottom, 0px))",
                    gap: 1,
                    // The primary action takes the width that is left; a Cancel
                    // beside it does not need half the bar.
                    "& > :last-child": { flex: 1 },
                    // Both measured 42px; the bar is the last thing a thumb
                    // reaches for, so it sits exactly on the floor.
                    "& > *": { minHeight: 44 },
                  }
                : null),
            }}
          >
            {actions}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
